<!-- Shared evidence display component for photo, location, and device info -->
<template>
  <div class="evidence-block">
    <div class="evidence-header">
      <component :is="icon" class="w-4 h-4" />
      <h5>{{ label }}</h5>
      <span v-if="timestamp" class="evidence-timestamp">
        {{ formatDateTime(timestamp) }}
      </span>
    </div>

    <div class="evidence-body">
      <!-- Photo + Location side by side -->
      <div class="evidence-row">
        <!-- Photo -->
        <div class="evidence-photo">
          <div v-if="photoUrl" class="photo-container">
            <img
              :src="resolvedPhotoUrl"
              :alt="`${label} photo`"
              class="photo-thumbnail"
              @click="viewingFullPhoto = true"
              @error="photoError = true"
            />
            <div class="photo-overlay" @click="viewingFullPhoto = true">
              <Maximize2 class="w-4 h-4" />
            </div>
          </div>
          <div v-else class="photo-placeholder">
            <Camera class="w-6 h-6" />
            <span>No photo</span>
          </div>
        </div>

        <!-- Location -->
        <div class="evidence-location">
          <div v-if="hasLocation" class="location-info">
            <div class="location-address">
              <MapPin class="w-4 h-4 text-blue-500" />
              <span>{{ address || 'Unknown address' }}</span>
            </div>
            <div class="location-coords">
              <span>{{ formatCoordinates(latitude, longitude) }}</span>
            </div>
          </div>
          <div v-else class="location-placeholder">
            <MapPinOff class="w-5 h-5" />
            <span>No location data</span>
          </div>
        </div>
      </div>

      <!-- Device info (collapsible) -->
      <div v-if="showDevice && deviceFingerprint" class="device-section">
        <button @click="deviceExpanded = !deviceExpanded" class="device-toggle">
          <Smartphone class="w-3.5 h-3.5" />
          <span>Device Info</span>
          <ChevronRight :class="['w-3.5 h-3.5 chevron', { expanded: deviceExpanded }]" />
        </button>
        <div v-if="deviceExpanded" class="device-details">
          <span class="device-fp">{{ deviceFingerprint }}</span>
        </div>
      </div>
    </div>

    <!-- Full-screen photo viewer -->
    <Teleport to="body">
      <div
        v-if="viewingFullPhoto && photoUrl"
        class="photo-viewer-overlay"
        @click.self="viewingFullPhoto = false"
      >
        <div class="photo-viewer-container">
          <img
            :src="resolvedPhotoUrl"
            :alt="`${label} photo - full view`"
            class="photo-viewer-img"
          />
          <button @click="viewingFullPhoto = false" class="photo-viewer-close">
            <X class="w-5 h-5" />
          </button>
          <div class="photo-viewer-caption">
            {{ label }} — {{ timestamp ? formatDateTime(timestamp) : '' }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import {
  MapPin, MapPinOff, Camera, Maximize2, X,
  Smartphone, ChevronRight, LogIn, LogOut, Shield
} from 'lucide-vue-next';
import dayjs from 'dayjs';

const props = defineProps({
  label: { type: String, required: true },
  photoUrl: { type: String, default: null },
  latitude: { type: [Number, String], default: null },
  longitude: { type: [Number, String], default: null },
  address: { type: String, default: null },
  deviceFingerprint: { type: String, default: null },
  timestamp: { type: String, default: null },
  showDevice: { type: Boolean, default: false },
  type: { type: String, default: 'clock_in' } // clock_in, clock_out, activation
});

const viewingFullPhoto = ref(false);
const deviceExpanded = ref(false);
const photoError = ref(false);

const icon = computed(() => {
  if (props.type === 'clock_out') return LogOut;
  if (props.type === 'activation') return Shield;
  return LogIn;
});

const hasLocation = computed(() => {
  return props.latitude != null && props.longitude != null;
});

const resolvedPhotoUrl = computed(() => {
  if (!props.photoUrl) return null;
  // If already a full URL, use as-is
  if (props.photoUrl.startsWith('http')) return props.photoUrl;
  // If relative path starting with uploads/, prepend /
  if (props.photoUrl.startsWith('uploads/')) return `/${props.photoUrl}`;
  // Otherwise prepend /uploads/attendance/
  return props.photoUrl;
});

const formatCoordinates = (lat, lng) => {
  if (lat == null || lng == null) return 'N/A';
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
};

const formatDateTime = (ts) => {
  if (!ts) return '';
  return dayjs(ts).format('MMM D, YYYY h:mm A');
};
</script>

<style scoped>
.evidence-block {
  background: #f9fafb;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.evidence-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.evidence-header h5 {
  font-size: 0.8rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
  flex: 1;
}

.evidence-timestamp {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.evidence-body {
  padding: 1rem;
}

.evidence-row {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

/* Photo */
.evidence-photo {
  flex-shrink: 0;
  width: 100px;
}

.photo-container {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  cursor: pointer;
}

.photo-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: all 0.2s ease;
}

.photo-container:hover .photo-overlay {
  background: rgba(0, 0, 0, 0.4);
  opacity: 1;
}

.photo-placeholder {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  border: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  gap: 0.25rem;
}

.photo-placeholder span {
  font-size: 0.65rem;
  font-weight: 500;
}

/* Location */
.evidence-location {
  flex: 1;
  min-width: 0;
}

.location-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.location-address {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.4;
}

.location-address svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.location-coords {
  font-size: 0.75rem;
  color: #6b7280;
  font-family: ui-monospace, monospace;
  padding-left: 1.5rem;
}

.location-placeholder {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9ca3af;
  font-size: 0.85rem;
  padding: 1rem 0;
}

/* Device */
.device-section {
  margin-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 0.75rem;
}

.device-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  color: #6b7280;
  padding: 0;
  font-weight: 500;
}

.device-toggle:hover {
  color: #374151;
}

.chevron {
  transition: transform 0.2s ease;
}

.chevron.expanded {
  transform: rotate(90deg);
}

.device-details {
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
}

.device-fp {
  font-size: 0.7rem;
  font-family: ui-monospace, monospace;
  color: #6b7280;
  word-break: break-all;
}

/* Full-screen photo viewer */
.photo-viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
}

.photo-viewer-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.photo-viewer-img {
  max-width: 100%;
  max-height: 85vh;
  border-radius: 8px;
}

.photo-viewer-close {
  position: absolute;
  top: -2.5rem;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  padding: 0.5rem;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.photo-viewer-close:hover {
  background: rgba(255, 255, 255, 0.4);
}

.photo-viewer-caption {
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  margin-top: 0.75rem;
}

@media (max-width: 640px) {
  .evidence-row {
    flex-direction: column;
  }

  .evidence-photo {
    width: 80px;
  }

  .photo-container,
  .photo-placeholder {
    width: 80px;
    height: 80px;
  }
}
</style>
